/**
 * Layouts in the editor (ADR 0037): one graphic with a layout per aspect. The canvas is the primary layout; every
 * other layout is a size plus per-element overrides (transform, size, anchor, visible, fontSize, fit) and per-keyframe
 * values. The editor shows one layout at a time as an ordinary scene (`viewOf`, scene-schema's `sceneForLayout`), so
 * every command works unchanged; `foldLayoutEdit` puts an edit made in that flat scene back:
 *
 * - an overridable property that differs from the primary becomes an override of this layout, and one that equals
 *   the primary again is cleared;
 * - everything else (text, colours, new elements, deletions, keyframe times and easings) goes to the shared base;
 * - a keyframe inserted in a layout gets the primary's current value in the base, and the layout's as an override.
 *
 * Timing is shared, so keyframe overrides (keyed by track and time) follow every move and are dropped with their
 * keyframe: `correspond` pairs the keyframes before and after an edit, and every layout's overrides are renamed by it.
 * `reconcileLayouts` does that for edits in the primary layout. Pure; unit-tested in test/layouts.test.ts.
 */
import {
  OVERRIDE_SIZE_KEYS,
  OVERRIDE_TRANSFORM_KEYS,
  layoutId,
  layoutsOf,
  overrideOf,
  sceneForLayout,
  timeKey,
  trackKey,
  type Element,
  type Keyframe,
  type KeyframeValue,
  type Layout,
  type LayoutOverride,
  type Phase,
  type Scene,
  type Timeline,
  type Track,
} from '@prism-graphics/scene-schema';
import { sampleTrack } from '@prism-graphics/runtime';
import type { PhaseRef } from './timeline.ts';
import { designValue, toHexColor } from './timeline.ts';
import { findElement, walk } from './tree.ts';

/** The primary layout's id (the canvas's reduced aspect ratio). */
export const primaryOf = (scene: Pick<Scene, 'canvas'>): string => layoutId(scene.canvas.width, scene.canvas.height);

/** Whether the graphic has layouts besides the primary. */
export const hasLayouts = (scene: Pick<Scene, 'layouts'>): boolean => (scene.layouts?.length ?? 0) > 0;

/** The layout `id` if the scene has it, else the primary's id. */
export function resolveLayout(scene: Scene, id: string | null | undefined): string {
  if (id && scene.layouts?.some((l) => l.id === id)) return id;
  return primaryOf(scene);
}

// ---------------------------------------------------------------------------------------------------------------
// Views

const views = new WeakMap<Scene, Map<string, Scene>>();
const sources = new WeakMap<Scene, Scene>();

/**
 * The scene as the editor shows layout `id`: the scene itself for the primary (layouts and all, so edits keep them),
 * else the flattened scene, memoised per scene and layout (a stable value for React) and remembered as a view of
 * `scene` (`sourceOf`).
 */
export function viewOf(scene: Scene, id: string): Scene {
  if (!hasLayouts(scene) || id === primaryOf(scene) || !scene.layouts!.some((l) => l.id === id)) return scene;
  let cache = views.get(scene);
  if (!cache) views.set(scene, (cache = new Map()));
  let view = cache.get(id);
  if (!view) {
    view = sceneForLayout(scene, id);
    cache.set(id, view);
    sources.set(view, scene);
  }
  return view;
}

/** The scene a view was made from (undefined for anything else). */
export const sourceOf = (view: Scene): Scene | undefined => sources.get(view);

// ---------------------------------------------------------------------------------------------------------------
// Overrides of one element

const TRANSFORM_KEYS = OVERRIDE_TRANSFORM_KEYS;
const SIZE_KEYS = OVERRIDE_SIZE_KEYS;

const same = (a: unknown, b: unknown): boolean => a === b || JSON.stringify(a) === JSON.stringify(b);

export { overrideOf };

/**
 * The base element for an edit made in a layout: the edited element with the primary's overridable properties. A
 * size limit removed in the layout (e.g. "max width" cleared) cannot be an override, so it is removed in the base too.
 */
function baseElement(el: Element, p: Element): Element {
  const size = { ...el.size } as Record<string, unknown>;
  for (const k of SIZE_KEYS) {
    if (el.size[k] === undefined) continue;
    if (p.size[k] === undefined) delete size[k];
    else size[k] = p.size[k];
  }
  const out = { ...el, transform: { ...p.transform }, size, visible: p.visible } as Element;
  if (p.anchor) out.anchor = { ...p.anchor };
  else delete out.anchor;
  if (out.type === 'text' && p.type === 'text') out.style = { ...out.style, fontSize: p.style.fontSize };
  if ((out.type === 'image' || out.type === 'video') && (p.type === 'image' || p.type === 'video')) out.fit = p.fit;
  return out;
}

function mapTree(elements: readonly Element[], fn: (el: Element) => Element): Element[] {
  return elements.map((el) => {
    const out = fn(el);
    if ((out.type === 'group' || out.type === 'clipMask') && (el.type === 'group' || el.type === 'clipMask')) return { ...out, children: mapTree(el.children, fn) } as Element;
    return out;
  });
}

// ---------------------------------------------------------------------------------------------------------------
// Phases, tracks and keyframes: which keyframe after an edit continues which keyframe before it

/** A phase of a timeline with an identity that survives removing a step or an action (its path does not). */
interface PhaseEntry {
  id: string;
  path: string;
  phase: Phase;
}

function phaseEntries(t: Timeline): PhaseEntry[] {
  const list: PhaseEntry[] = [
    { id: 'in', path: 'timeline.in', phase: t.in },
    { id: 'out', path: 'timeline.out', phase: t.out },
    { id: 'update', path: 'timeline.update', phase: { duration: t.update.duration, tracks: t.update.tracks } },
  ];
  t.steps.forEach((s, i) => {
    if (s.enter) list.push({ id: `step:${s.id}:enter`, path: `timeline.steps.${i}.enter`, phase: s.enter });
    if (s.loop) list.push({ id: `step:${s.id}:loop`, path: `timeline.steps.${i}.loop`, phase: s.loop });
  });
  for (const [fid, tr] of Object.entries(t.update.fields)) if (tr.mode === 'phase') list.push({ id: `field:${fid}`, path: `timeline.update.fields.${fid}`, phase: tr.phase });
  t.customActions.forEach((a, i) => list.push({ id: `custom:${a.id}`, path: `timeline.customActions.${i}.phase`, phase: a.phase }));
  return list;
}

/** The phase path of a timeline phase reference (the first part of a keyframe override's track key). */
export function phasePathOf(scene: Scene, ref: PhaseRef): string | undefined {
  switch (ref.kind) {
    case 'in':
      return 'timeline.in';
    case 'out':
      return 'timeline.out';
    case 'update':
      return 'timeline.update';
    case 'step':
      return `timeline.steps.${ref.index}.enter`;
    case 'loop':
      return `timeline.steps.${ref.index}.loop`;
    case 'field':
      return `timeline.update.fields.${ref.field}`;
    case 'custom':
      return scene.timeline.customActions[ref.index] ? `timeline.customActions.${ref.index}.phase` : undefined;
  }
}

const trackIdOf = (tr: Track): string => `${tr.element}/${tr.property}/${tr.stagger ? JSON.stringify(tr.stagger) : ''}`;

/** Pairs of track indices (before, after): the same element, property and stagger, in order of occurrence. */
function pairTracks(a: readonly Track[], b: readonly Track[]): Array<[number, number]> {
  const seen = new Map<string, number[]>();
  a.forEach((tr, i) => {
    const k = trackIdOf(tr);
    seen.set(k, [...(seen.get(k) ?? []), i]);
  });
  const pairs: Array<[number, number]> = [];
  b.forEach((tr, j) => {
    const list = seen.get(trackIdOf(tr));
    const i = list?.shift();
    if (i !== undefined) pairs.push([i, j]);
  });
  return pairs;
}

const near = (a: number, b: number) => Math.abs(a - b) < 0.5;

/**
 * Pairs of keyframe indices (before, after) in one track: the keyframe each one after the edit continues. Same times:
 * by index. Otherwise keyframes that kept their time, value and easing pair first (they did not move); the rest pair
 * in order when as many are left on both sides (a move, however far), else by time (an insert or a delete).
 */
export function pairKeys(a: readonly Keyframe[], b: readonly Keyframe[]): Array<[number, number]> {
  if (a.length === b.length && a.every((k, i) => near(k.time, b[i]!.time))) return a.map((_, i) => [i, i]);
  const usedA = new Set<number>();
  const usedB = new Set<number>();
  const pairs: Array<[number, number]> = [];
  b.forEach((kb, j) => {
    const i = a.findIndex((ka, i2) => !usedA.has(i2) && near(ka.time, kb.time) && same(ka.value, kb.value) && ka.easing === kb.easing);
    if (i < 0) return;
    usedA.add(i);
    usedB.add(j);
    pairs.push([i, j]);
  });
  const restA = a.map((_, i) => i).filter((i) => !usedA.has(i));
  const restB = b.map((_, j) => j).filter((j) => !usedB.has(j));
  if (restA.length === restB.length) restA.forEach((i, n) => pairs.push([i, restB[n]!]));
  else
    for (const j of restB) {
      const i = restA.find((i2) => !usedA.has(i2) && near(a[i2]!.time, b[j]!.time));
      if (i === undefined) continue;
      usedA.add(i);
      pairs.push([i, j]);
    }
  return pairs.sort((x, y) => x[1] - y[1]);
}

/** One track after an edit, with the track it continues (if any) and, per keyframe, the keyframe it continues. */
interface TrackMatch {
  path: string;
  track: Track;
  before?: { path: string; track: Track; keys: Map<number, number> };
}

/** Every track after the edit `prev` → `next`, matched to the tracks and keyframes before it. */
function correspond(prev: Timeline, next: Timeline): TrackMatch[] {
  const before = new Map(phaseEntries(prev).map((e) => [e.id, e]));
  const out: TrackMatch[] = [];
  for (const e of phaseEntries(next)) {
    const b = before.get(e.id);
    const pairs = b ? new Map(pairTracks(b.phase.tracks, e.phase.tracks).map(([i, j]) => [j, i])) : new Map<number, number>();
    e.phase.tracks.forEach((track, j) => {
      const i = pairs.get(j);
      if (!b || i === undefined) return out.push({ path: e.path, track });
      const old = b.phase.tracks[i]!;
      const keys = new Map(pairKeys(old.keyframes, track.keyframes).map(([ki, kj]) => [kj, ki]));
      out.push({ path: e.path, track, before: { path: b.path, track: old, keys } });
    });
  }
  return out;
}

/** "trackKey@time" before → [trackKey, time] after, for renaming keyframe overrides. */
type Rename = Map<string, [string, string]>;

function renames(matches: readonly TrackMatch[]): Rename {
  const map: Rename = new Map();
  for (const m of matches) {
    if (!m.before) continue;
    const from = trackKey(m.before.path, m.before.track);
    const to = trackKey(m.path, m.track);
    for (const [kj, ki] of m.before.keys) map.set(`${from}@${timeKey(m.before.track.keyframes[ki]!.time)}`, [to, timeKey(m.track.keyframes[kj]!.time)]);
  }
  return map;
}

function mapPhases(t: Timeline, fn: (phase: Phase, path: string) => Phase): Timeline {
  const steps = t.steps.map((s, i) => ({ ...s, ...(s.enter ? { enter: fn(s.enter, `timeline.steps.${i}.enter`) } : {}), ...(s.loop ? { loop: fn(s.loop, `timeline.steps.${i}.loop`) } : {}) }));
  const update = fn({ duration: t.update.duration, tracks: t.update.tracks }, 'timeline.update');
  const fields = Object.fromEntries(Object.entries(t.update.fields).map(([k, v]) => [k, v.mode === 'phase' ? { ...v, phase: fn(v.phase, `timeline.update.fields.${k}`) } : v]));
  return {
    ...t,
    in: fn(t.in, 'timeline.in'),
    out: fn(t.out, 'timeline.out'),
    steps,
    update: { ...t.update, tracks: update.tracks, fields },
    customActions: t.customActions.map((a, i) => ({ ...a, phase: fn(a.phase, `timeline.customActions.${i}.phase`) })),
  };
}

// ---------------------------------------------------------------------------------------------------------------
// A layout's overrides from a flat scene

/**
 * The overrides that make `flat` (a scene in some layout, with the same elements and timeline structure) out of the
 * primary `base`: overridable properties and keyframe values that differ.
 */
export function overridesFrom(base: Scene, flat: Scene): Pick<Layout, 'overrides' | 'keyframes'> {
  const overrides: NonNullable<Layout['overrides']> = {};
  walk(flat.elements, (el) => {
    const p = findElement(base, el.id);
    if (!p || p.type !== el.type) return;
    const o = overrideOf(p, el);
    if (o) overrides[el.id] = o;
  });
  const keyframes: NonNullable<Layout['keyframes']> = {};
  for (const m of correspond(base.timeline, flat.timeline)) {
    if (!m.before) continue;
    for (const [kj, ki] of m.before.keys) {
      const v = m.track.keyframes[kj]!.value;
      if (same(v, m.before.track.keyframes[ki]!.value)) continue;
      const key = trackKey(m.before.path, m.before.track);
      (keyframes[key] ??= {})[timeKey(m.before.track.keyframes[ki]!.time)] = typeof v === 'object' ? { ...v } : v;
    }
  }
  return { ...(Object.keys(overrides).length ? { overrides } : {}), ...(Object.keys(keyframes).length ? { keyframes } : {}) };
}

/**
 * A layout after its base changed (`base`, the new primary): overrides of elements that are gone (or no longer take
 * them) dropped, a font size below a raised minimum raised, and keyframe overrides renamed with their keyframes
 * (`rename`; dropped with them). Returns `l` itself when nothing changed.
 */
function pruneLayout(l: Layout, base: Scene, rename: Rename | undefined): Layout {
  let changed = false;
  let overrides: Layout['overrides'];
  if (l.overrides) {
    overrides = {};
    for (const [id, o] of Object.entries(l.overrides)) {
      const el = findElement(base, id);
      if (!el) {
        changed = true;
        continue;
      }
      const next = { ...o };
      if (next.fontSize !== undefined && el.type !== 'text') delete next.fontSize;
      else if (next.fontSize !== undefined && el.type === 'text' && el.overflow.mode === 'shrink' && el.overflow.minFontSize > next.fontSize) next.fontSize = el.overflow.minFontSize;
      if (next.fit !== undefined && el.type !== 'image' && el.type !== 'video') delete next.fit;
      if (!same(next, o)) changed = true;
      if (Object.keys(next).length) overrides[id] = next;
    }
  }
  let keyframes: Layout['keyframes'];
  if (l.keyframes) {
    if (!rename) keyframes = l.keyframes;
    else {
      keyframes = {};
      for (const [key, values] of Object.entries(l.keyframes)) {
        for (const [time, v] of Object.entries(values)) {
          const to = rename.get(`${key}@${time}`);
          if (!to) continue;
          (keyframes[to[0]] ??= {})[to[1]] = v;
        }
      }
      if (!same(keyframes, l.keyframes)) changed = true;
    }
  }
  if (!changed) return l;
  const { overrides: _o, keyframes: _k, ...rest } = l;
  return { ...rest, ...(overrides && Object.keys(overrides).length ? { overrides } : {}), ...(keyframes && Object.keys(keyframes).length ? { keyframes } : {}) };
}

// ---------------------------------------------------------------------------------------------------------------
// Edits

/** The primary's value of a property at `time` (its track before the edit sampled), in the form `like` has. */
function primaryValueAt(scene: Scene, track: Track, primary: Track | undefined, time: number, like: KeyframeValue): KeyframeValue {
  const el = findElement(scene, track.element);
  const design = el ? designValue(el, track.property) : 0;
  const v = primary ? sampleTrack(primary, time, design) : design;
  if (typeof like === 'object') return { rel: typeof v === 'number' && typeof design === 'number' ? Math.round((v - design) * 1000) / 1000 : 0 };
  if (typeof like === 'string') return toHexColor(typeof v === 'string' ? v : String(design));
  return typeof v === 'number' ? Math.round(v * 1000) / 1000 : like;
}

/**
 * Put an edit made in layout `id` (`edited`, a change of `viewOf(scene, id)`) back into the scene (see the header).
 * The layout keeps its size: a layout's format changes with `replaceLayout`. In the primary layout the edit is the
 * scene, and `reconcileLayouts` keeps the other layouts valid.
 */
export function foldLayoutEdit(scene: Scene, id: string, edited: Scene): Scene {
  if (!hasLayouts(scene) || id === primaryOf(scene)) return reconcileLayouts(scene, edited);
  const layout = scene.layouts!.find((l) => l.id === id);
  if (!layout) throw new Error(`no layout "${id}"`);
  const prevView = viewOf(scene, id);
  if (edited === prevView) return scene;

  const elements = mapTree(edited.elements, (el) => {
    const p = findElement(scene, el.id);
    return p && p.type === el.type ? baseElement(el, p) : el;
  });
  // Timing is the edit's; values are the primary's (the keyframe each one continues, else its value at that time).
  const matches = correspond(prevView.timeline, edited.timeline);
  const byTrack = new Map(matches.map((m) => [m.track, m]));
  // The view's tracks are the primary's, at the same places (the view only has other values).
  const primaryTracks = new Map<Track, Track>();
  const primaryPhases = new Map(phaseEntries(scene.timeline).map((e) => [e.id, e.phase]));
  for (const e of phaseEntries(prevView.timeline)) {
    const p = primaryPhases.get(e.id);
    e.phase.tracks.forEach((tr, i) => p?.tracks[i] && primaryTracks.set(tr, p.tracks[i]));
  }
  const timeline = mapPhases(edited.timeline, (phase) => {
    let changed = false;
    const tracks = phase.tracks.map((tr) => {
      const m = byTrack.get(tr);
      if (!m) return tr;
      const primary = m.before ? primaryTracks.get(m.before.track) : undefined;
      const keyframes = tr.keyframes.map((kf, kj) => {
        const ki = m.before?.keys.get(kj);
        const value = ki !== undefined && primary ? primary.keyframes[ki]!.value : primaryValueAt(scene, tr, primary, kf.time, kf.value);
        if (same(value, kf.value)) return kf;
        changed = true;
        return { ...kf, value: typeof value === 'object' ? { ...value } : value };
      });
      return keyframes.every((k, i) => k === tr.keyframes[i]) ? tr : { ...tr, keyframes };
    });
    return changed ? { ...phase, tracks } : phase;
  });
  const { layouts: _l, ...rest } = edited;
  const base: Scene = { ...rest, canvas: { ...edited.canvas, width: scene.canvas.width, height: scene.canvas.height }, elements, timeline };

  const own = overridesFrom(base, { ...edited, canvas: base.canvas });
  const { overrides: _o, keyframes: _k, ...shape } = layout;
  const rename = renames(matches);
  const layouts = scene.layouts!.map((l) => (l.id === id ? { ...shape, ...own } : pruneLayout(l, base, rename)));
  return { ...base, layouts };
}

/**
 * An edit in the primary layout (`next`, a change of `prev` that kept its layouts): the other layouts' overrides of
 * elements that are gone are dropped and their keyframe overrides follow the keyframes (renamed or dropped).
 */
export function reconcileLayouts(prev: Scene, next: Scene): Scene {
  if (!hasLayouts(next) || next === prev) return next;
  const timed = next.timeline !== prev.timeline;
  if (!timed && next.elements === prev.elements && next.layouts === prev.layouts) return next;
  const rename = timed ? renames(correspond(prev.timeline, next.timeline)) : undefined;
  const layouts = next.layouts!.map((l) => pruneLayout(l, next, rename));
  return layouts.every((l, i) => l === next.layouts![i]) ? next : { ...next, layouts };
}

/**
 * Run a command in every layout (each in its own flat scene, with its own geometry): grouping, ungrouping,
 * reparenting and duplicating place elements by where they are on screen, which differs per layout. The primary's
 * result is the new base and each other layout's result becomes its overrides.
 */
export function applyInLayouts(scene: Scene, fn: (flat: Scene, id: string) => Scene): Scene {
  if (!hasLayouts(scene)) return fn(scene, primaryOf(scene));
  const primaryFlat = sceneForLayout(scene);
  const result = fn(primaryFlat, primaryOf(scene));
  if (result === primaryFlat) return scene;
  const { layouts: _l, ...base } = result;
  const layouts = scene.layouts!.map((l) => {
    const flat = fn(sceneForLayout(scene, l.id), l.id);
    const { overrides: _o, keyframes: _k, ...shape } = l;
    return { ...shape, ...overridesFrom(base as Scene, flat) };
  });
  return { ...base, layouts } as Scene;
}

// ---------------------------------------------------------------------------------------------------------------
// Adding, re-adapting and removing layouts

/** A layout made from `flat` (the graphic adapted to the layout's size, e.g. by adaptCanvas) over the primary. */
function layoutFrom(scene: Scene, flat: Scene, label?: string): Layout {
  const { width, height } = flat.canvas;
  return { id: layoutId(width, height), ...(label ? { label } : {}), width, height, ...overridesFrom(sceneForLayout(scene), flat) };
}

/** Add a layout made from `flat` (after the others). */
export function addLayout(scene: Scene, flat: Scene): Scene {
  return { ...scene, layouts: [...(scene.layouts ?? []), layoutFrom(scene, flat)] };
}

/** Replace layout `id` by one made from `flat` (another size re-adapts it; its id follows its aspect). */
export function replaceLayout(scene: Scene, id: string, flat: Scene): Scene {
  if (!scene.layouts?.some((l) => l.id === id)) return scene;
  return { ...scene, layouts: scene.layouts.map((l) => (l.id === id ? layoutFrom(scene, flat, l.id === layoutId(flat.canvas.width, flat.canvas.height) ? l.label : undefined) : l)) };
}

/** Remove layout `id` (not the primary). The last one removed leaves a single-layout scene, without `layouts`. */
export function removeLayout(scene: Scene, id: string): Scene {
  if (!scene.layouts?.some((l) => l.id === id)) return scene;
  const layouts = scene.layouts.filter((l) => l.id !== id);
  if (layouts.length) return { ...scene, layouts };
  const { layouts: _l, ...rest } = scene;
  return rest as Scene;
}

/** Whether a size has the aspect of one of the scene's layouts (except `except`): a graphic has one per aspect. */
export function aspectTaken(scene: Scene, size: { width: number; height: number }, except?: string): boolean {
  return layoutsOf(scene).some((l) => l.id !== except && Math.abs(l.width / l.height - size.width / size.height) / (size.width / size.height) < 0.005);
}

// ---------------------------------------------------------------------------------------------------------------
// What a layout overrides

/** An overridable property group the inspector shows and resets: transform keys, size keys or a whole property. */
export type OverrideKey = `transform.${(typeof TRANSFORM_KEYS)[number]}` | `size.${(typeof SIZE_KEYS)[number]}` | 'anchor' | 'visible' | 'fontSize' | 'fit';

/** The overridden properties of an element in layout `id` ("transform.x", "size.width", "fontSize", …). */
export function overriddenKeys(scene: Scene, id: string, elementId: string): Set<OverrideKey> {
  const o = scene.layouts?.find((l) => l.id === id)?.overrides?.[elementId];
  const keys = new Set<OverrideKey>();
  if (!o) return keys;
  for (const k of Object.keys(o.transform ?? {})) keys.add(`transform.${k}` as OverrideKey);
  for (const k of Object.keys(o.size ?? {})) keys.add(`size.${k}` as OverrideKey);
  for (const k of ['anchor', 'visible', 'fontSize', 'fit'] as const) if (o[k] !== undefined) keys.add(k);
  return keys;
}

/** Clear overrides of an element in layout `id` ("Reset to primary"): it follows the primary there again. */
export function resetOverrides(scene: Scene, id: string, elementId: string, keys: readonly OverrideKey[]): Scene {
  const l = scene.layouts?.find((x) => x.id === id);
  const o = l?.overrides?.[elementId];
  if (!l || !o) return scene;
  const next: LayoutOverride = structuredClone(o);
  for (const k of keys) {
    const [group, prop] = k.split('.') as [string, string | undefined];
    if (prop && (group === 'transform' || group === 'size')) {
      const g = next[group] as Record<string, unknown> | undefined;
      if (g) delete g[prop];
      if (g && Object.keys(g).length === 0) delete next[group];
    } else delete next[group as 'anchor' | 'visible' | 'fontSize' | 'fit'];
  }
  if (same(next, o)) return scene;
  const overrides = { ...l.overrides };
  if (Object.keys(next).length) overrides[elementId] = next;
  else delete overrides[elementId];
  const { overrides: _o, ...rest } = l;
  const layout = { ...rest, ...(Object.keys(overrides).length ? { overrides } : {}) };
  return { ...scene, layouts: scene.layouts!.map((x) => (x === l ? layout : x)) };
}

/** Whether layout `id` overrides the value of a keyframe (the timeline shows it outlined). */
export function keyOverridden(scene: Scene, id: string, path: string | undefined, track: Track, time: number): boolean {
  if (!path) return false;
  const values = scene.layouts?.find((l) => l.id === id)?.keyframes?.[trackKey(path, track)];
  return values !== undefined && values[timeKey(time)] !== undefined;
}
